import {omitProperties, onlyOwnProperties} from '@/lib/ReusableFunctions/Object';
import {mappedFields} from '@/lib/StandardFields';
import {aggregators} from '../aggregators/parcel';
import {runAggregators, runDeleteFunctions} from './shared';

import {InfoWindowContent} from '../InfoWindowContent';
import {DisplayField} from '../DisplayField';

/**
 * createRepParcelContent - given a parcel, create infoWindowContent
 * @param {Object} parcel - a parcel to make infoWindowContent for
 * @return {InfoWindowContent} - the infoWindowContent for a parcel
 */
function createParcelContent(parcel) {
  try {
    const [aggregatedFields, deleteFunctions] = runAggregators(aggregators, parcel);

    const displayFields = [];
    const omitFields = ['parcel_id', 'rausa_id', 'location'];
    const metadata = omitProperties(onlyOwnProperties(parcel), ...omitFields);
    mappedFields.parcel.forEach((field) => {
      // If a field is already aggregated, push it
      if (aggregatedFields[field.id]) {
        displayFields.push(aggregatedFields[field.id]);
        return;
      }
      // If a field was supposed to be aggregated but did not appear in the aggregatedFields object,
      // That means that there is nothing to add so we can skip it
      if (aggregators.map((aggregator) => aggregator.name).includes(field.id)) return;

      // Every field except for the acreage is editable
      const editable = field.id !== 'acreageCalculated';

      // Otherwise, push a new display field and a delete function for the original value
      displayFields.push(new DisplayField({
        label: field.label,
        unit: field.formatUnit ?? field.unit,
        allowEdit: editable,
        allowLabelEdit: editable,
        allowDelete: true,
        value: parcel[field.reportAllKey],
      }));
      deleteFunctions.push((blob) => delete blob[field.reportAllKey]);
    });

    runDeleteFunctions(deleteFunctions, metadata);

    const contentData = {
      type: 'parcel',
      title: parcel.owner ?? ' ',
      id: parcel.parcel_id ?? parcel.rausa_id, // Some parcels don't have a parcel_id
      metadata,
      displayFields,
      host: {setHighlight: async (state) => parcel.graphic.setParcelHighlight(parcel.robust_id, state)},
    };

    return new InfoWindowContent(contentData);
  } catch (error) {
    console.warn('Error creating parcel info window content', error);
    return null;
  }
}

export {
  createParcelContent,
};
